/**
 * The board PANEL — desktop-only layout state (design doc §11.3).
 *
 * `Board.tsx`/`surfaceStore.ts` already decide WHAT is on the board; this
 * store decides whether the panel that HOLDS the board is on screen at all,
 * and whether it is open or the visitor collapsed it by hand. Three rules:
 *
 * **(a) the panel does not exist until the board's first non-structural
 * element does.** `everAppeared` flips once, from `noticeContent()`, and
 * never back — `App.tsx` mounts `<section className="app__board">` on
 * `everAppeared`, not on "does the board currently have content" (which
 * would flicker the section in and out around a transient empty board).
 *
 * **(b) auto-open only if the visitor never closed it by hand.**
 * `noticeContent()` opens the panel on the FIRST call (the same call that
 * flips `everAppeared`) unless `closedManually` is already set — reopening
 * on every later card would be the panel stealing the screen back after a
 * visitor deliberately put it away (§11.3: "доска, закрытая пять минут
 * назад, распахивалась бы на каждой новой карточке").
 *
 * **(c) `closedManually` mirrors the visitor's LAST explicit action, and
 * that is what survives a reload.** `toggle()` always persists — closing
 * writes `ph.panel.closed.<threadId>` ('1'), reopening deletes the key —
 * the same `localStorage`-under-`try/catch` idiom `activityStore.ts` uses
 * for `ph.activity.<threadId>`, read back by `restore()` on the "coming
 * back to a thread" effect in `App.tsx`. **Found on review (!280):** the
 * first draft only wrote on close, so the key meant "was ever closed",
 * never cleared by a reopen — close → reopen → F5 came back CLOSED, the
 * one action the visitor took most recently silently discarded. Note that
 * within one live session this changes nothing observable: `noticeContent()`
 * is a no-op past its first call regardless of `closedManually` (a), so the
 * flag's only real audience is the NEXT reload.
 *
 * Mobile is untouched by any of this (§11.3: "ниже 1024px ничего не
 * менять") — `App.tsx` never reads this store below the 1024px breakpoint.
 */

import { create } from 'zustand';

const PERSIST_PREFIX = 'ph.panel.closed.';

/** Best-effort write; a session that cannot persist just re-opens (or stays
 *  auto-opened) after a reload, which is a valid, honestly-degraded state —
 *  the same posture `activityStore.persist` takes. */
function persistClosed(threadId: string, closedManually: boolean): void {
  try {
    if (closedManually) window.localStorage.setItem(PERSIST_PREFIX + threadId, '1');
    else window.localStorage.removeItem(PERSIST_PREFIX + threadId);
  } catch {
    /* localStorage absent, full or blocked */
  }
}

function readClosed(threadId: string): boolean {
  try {
    return window.localStorage.getItem(PERSIST_PREFIX + threadId) === '1';
  } catch {
    return false;
  }
}

export type BoardPanelState = {
  /** Has the panel ever had something to show, this thread? Gates whether
   *  `App.tsx` mounts the `<section>` at all (§11.3). */
  everAppeared: boolean;
  /** Is the panel currently expanded? Meaningless before `everAppeared`. */
  open: boolean;
  /** Mirrors the last `toggle()` (c); read by `noticeContent()` (b),
   *  written and persisted by `toggle()`. */
  closedManually: boolean;

  /** Call whenever the board's own content becomes non-empty (`App.tsx`,
   *  keyed off `hasBoardContent`). Idempotent past the first call — a
   *  second, third, … card does nothing here (a). */
  noticeContent: () => void;
  /** The header's toggle button. `threadId` is passed in rather than read
   *  from `sessionStore` — same decoupling `activityStore.finish` uses —
   *  so this store never needs to know who else exists. */
  toggle: (threadId: string) => void;
  /** Reads the sticky flag for `threadId` — call once per thread, same spot
   *  `activityStore.restore` is called from. */
  restore: (threadId: string) => void;
  /** A new thread starts with no opinion of its own. */
  reset: () => void;

  /** «Открыть на карте» under an answer (`chat/TurnCards.tsx`): which board
   *  card to bring into view. `at` makes a second tap on the same card a new
   *  request. `App.tsx` answers it — the mobile pane, or the panel — and then
   *  scrolls; this store only carries the ask, so a chat card never needs to
   *  know the layout it is in. */
  focusRequest: { id: string; at: number } | null;
  requestFocus: (id: string) => void;
  /** Opens a collapsed panel because the visitor ASKED to see something on it
   *  — the same explicit action as `toggle()` to open, persisted the same way
   *  (c), and a no-op when it is already open. */
  reveal: (threadId: string) => void;
};

export const useBoardPanel = create<BoardPanelState>((set, get) => ({
  everAppeared: false,
  open: false,
  closedManually: false,

  noticeContent: () => {
    if (get().everAppeared) return; // (a): only the first call does anything
    set((state) => ({ everAppeared: true, open: !state.closedManually }));
  },

  toggle: (threadId) => {
    const open = !get().open;
    const closedManually = !open; // (c): mirrors the action just taken
    set({ open, closedManually });
    persistClosed(threadId, closedManually); // unconditional — opening clears the key too
  },

  restore: (threadId) => set({ closedManually: readClosed(threadId) }),

  reset: () => set({ everAppeared: false, open: false, closedManually: false, focusRequest: null }),

  focusRequest: null,

  requestFocus: (id) => set({ focusRequest: { id, at: Date.now() } }),

  reveal: (threadId) => {
    if (get().open) return;
    set({ open: true, closedManually: false });
    persistClosed(threadId, false);
  },
}));
